import { ReactNode } from "react";

const widthPattern = [58, 76, 46, 68, 54, 82];

interface Props {
  rows?: number;
  columns?: number;
  fill?: boolean;
}

const TableSkeleton = ({ rows = 3, columns = 4, fill = false }: Props) => {
  const rowCount = fill
    ? Math.min(Math.max(rows, 2), 8)
    : Math.min(Math.max(rows, 2), 4);
  const columnCount = Math.min(Math.max(columns, 2), fill ? 8 : 5);

  return (
    <div
      className={`w-full min-w-0 max-w-full ${fill ? "h-full" : ""}`}
      aria-busy="true"
      aria-label="در حال بارگذاری جدول"
    >
      <div
        className={`relative flex w-full min-w-0 flex-col overflow-hidden rounded border-2 border-sky-100 bg-white ${fill ? "h-full" : ""}`}
      >
        <div
          className="grid shrink-0 items-center gap-2 border-b border-sky-100 bg-sky-50 px-2 py-2"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: columnCount }, (_, index) => (
            <div key={index} className="flex min-w-0 justify-center">
              <span
                className="block h-2 rounded-full bg-sky-200"
                style={{ width: `${widthPattern[index % widthPattern.length]}%` }}
              />
            </div>
          ))}
        </div>
        <div className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}>
          {Array.from({ length: rowCount }, (_, row) => (
            <div
              key={row}
              className={`grid items-center gap-2 px-2 ${fill ? "min-h-0 flex-1" : "py-2"}`}
              style={{
                gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
              }}
            >
              {Array.from({ length: columnCount }, (_, column) => (
                <div key={column} className="flex min-w-0 justify-center">
                  <span
                    className="block h-2 rounded-full bg-slate-200"
                    style={{
                      width: `${widthPattern[(row + column) % widthPattern.length]}%`,
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="table-skeleton-shimmer" />
      </div>
    </div>
  );
};

interface FrameProps {
  isLoading: boolean;
  columns?: number;
  rows?: number;
  table: ReactNode;
  footer?: ReactNode;
}

export const TableLoadingFrame = ({
  isLoading,
  columns,
  rows,
  table,
  footer,
}: FrameProps) => {
  return (
    <div className="relative flex w-full min-w-0 flex-1 flex-col items-stretch self-stretch justify-start gap-3 p-3">
      <div className="relative w-full min-w-0 self-stretch">
        {table ? (
          <div className={isLoading ? "invisible" : undefined}>{table}</div>
        ) : (
          <TableSkeleton columns={columns} rows={rows} />
        )}
        {isLoading && table ? (
          <div className="absolute inset-0 z-10 overflow-hidden bg-white">
            <TableSkeleton fill columns={columns} rows={rows} />
          </div>
        ) : null}
      </div>
      {footer}
    </div>
  );
};

export default TableSkeleton;
